// src/services/photoSharingService.ts
import {
   PhotoSharingRequest,
   PhotoSharingResponse,
   PhotoSharingRecipient,
   SelectedPhoto,
} from '../types/photoSharing';
import { getAuthHeaders } from '../utils/auth';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

export class PhotoSharingService {
   /**
    * Share photos with selected layout to participants and non-participants
    */
   static async sharePhotos(request: PhotoSharingRequest): Promise<PhotoSharingResponse> {
      try {
         const headers = await getAuthHeaders();
         const response = await fetch(`${API_BASE_URL}/api/photo-sharing/share`, {
            method: 'POST',
            headers,
            body: JSON.stringify({
               entityType: request.entityType,
               entityId: request.entityId,
               entityTitle: request.entityTitle,
               selectedPhotos: request.sharingData.selectedPhotos,
               layout: request.sharingData.layout,
               recipients: request.sharingData.recipients,
               message: request.sharingData.message,
               subject: request.sharingData.subject,
            }),
         });

         if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Failed to share photos');
         }

         return await response.json();
      } catch (error) {
         console.error('Error sharing photos:', error);
         throw error;
      }
   }

   /**
    * Get participants for an entity (trip, event, or album)
    */
   static async getParticipants(
      entityType: 'trip' | 'event' | 'album',
      entityId: string
   ): Promise<{ participants: PhotoSharingRecipient[]; nonParticipants: PhotoSharingRecipient[] }> {
      try {
         const headers = await getAuthHeaders();
         const response = await fetch(
            `${API_BASE_URL}/api/photo-sharing/${entityType}/${entityId}/participants`,
            {
               method: 'GET',
               headers,
            }
         );

         if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Failed to fetch participants');
         }

         return await response.json();
      } catch (error) {
         console.error('Error fetching participants:', error);
         throw error;
      }
   }

   /**
    * Get photos for an entity (trip, event, or album)
    */
   static async getPhotos(
      entityType: 'trip' | 'event' | 'album',
      entityId: string
   ): Promise<{ photos: SelectedPhoto[] }> {
      try {
         const headers = await getAuthHeaders();
         const response = await fetch(
            `${API_BASE_URL}/api/photo-sharing/${entityType}/${entityId}/photos`,
            {
               method: 'GET',
               headers,
            }
         );

         if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Failed to fetch photos');
         }

         return await response.json();
      } catch (error) {
         console.error('Error fetching photos:', error);
         throw error;
      }
   }

   /**
    * Validate email address
    */
   static validateEmail(email: string): boolean {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(email);
   }

   /**
    * Format recipient for display
    */
   static formatRecipientName(recipient: PhotoSharingRecipient): string {
      return recipient.displayName || recipient.email.split('@')[0];
   }

   /**
    * Check if layout supports the number of selected photos
    */
   static validatePhotoCount(
      layout: string,
      photoCount: number
   ): { valid: boolean; message?: string } {
      const layoutLimits: Record<string, { min: number; max: number }> = {
         'grid-2x2': { min: 1, max: 4 },
         'grid-3x3': { min: 1, max: 9 },
         'collage-magazine': { min: 3, max: 8 },
         'collage-polaroid': { min: 2, max: 6 },
         'timeline-vertical': { min: 2, max: 10 },
         'timeline-horizontal': { min: 2, max: 8 },
         'single-large': { min: 1, max: 1 },
         'mosaic-mixed': { min: 4, max: 12 },
      };

      const limits = layoutLimits[layout];
      if (!limits) {
         return { valid: true }; // Unknown layout, allow any count
      }

      if (photoCount < limits.min) {
         return {
            valid: false,
            message: `This layout requires at least ${limits.min} photo${limits.min > 1 ? 's' : ''}`,
         };
      }

      if (photoCount > limits.max) {
         return {
            valid: false,
            message: `This layout supports maximum ${limits.max} photos`,
         };
      }

      return { valid: true };
   }
}
